import React, { useState } from 'react';
import { 
  X, 
  Check, 
  ArrowRight, 
  ShieldCheck, 
  Loader2, 
  Zap, 
  HelpCircle,
  FileText,
  Calendar,
  Building,
  Info
} from 'lucide-react';
import { db, doc, setDoc, serverTimestamp } from '../firebase';
import { useAuth } from '../AuthContext';
import { useSavedSolicitations } from '../SavedSolicitationsContext';
import { Card, Button, cn } from './SaaSUIHelper';

interface SaaSInAppBillingProps {
  onClose: () => void;
}

export const SaaSInAppBilling: React.FC<SaaSInAppBillingProps> = ({ onClose }) => {
  const { user, vendorProfile } = useAuth();
  const { addToast } = useSavedSolicitations();
  const [selectedPlan, setSelectedPlan] = useState<any | null>(null);
  const [step, setStep] = useState<'tiers' | 'checkout' | 'processing' | 'success'>('tiers');
  const [paypalEmail, setPaypalEmail] = useState(vendorProfile?.paypalEmail || '');
  const [paypalPassword, setPaypalPassword] = useState('');

  const plans = [
    {
      id: 'starter',
      name: 'Starter Plan',
      tagline: 'Ideal for small subcontracting teams.',
      price: '$49/mo',
      features: [
        'Single-State portal tracking',
        '5 AI RFX responses per month',
        'Basic local company profile management',
        'Email alerts for matching bids',
        '1 User seat included'
      ]
    },
    {
      id: 'pro',
      name: 'Professional Plan',
      tagline: 'Best for growing multi-state bidders.',
      price: '$149/mo',
      features: [
        'All 50 States portal tracking (Crawlers)',
        'Unlimited AI RFX Response generations',
        'Access to full RFX Procurement Database',
        'Google Workspace Integration (Docs, Drive)',
        'Google Calendar & Reminders scheduler',
        '3 User seats included'
      ],
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise Plan',
      tagline: 'Custom analytics for prime contractors.',
      price: '$499/mo',
      features: [
        'Custom web crawler agents on-demand',
        'Dedicated Gemini API throughput allocation',
        'Interactive Win-Rate Predictive Analytics',
        'White-labeled bid response exporting',
        'Unlimited User seats'
      ]
    }
  ];

  // Static mock invoices for professional tax & accounting details
  const mockInvoices = [
    { id: 'INV-2026-0041', date: 'June 15, 2026', amount: '$149.00', status: 'Paid', method: 'PayPal' },
    { id: 'INV-2026-0028', date: 'May 15, 2026', amount: '$149.00', status: 'Paid', method: 'PayPal' },
    { id: 'INV-2026-0012', date: 'April 15, 2026', amount: '$149.00', status: 'Paid', method: 'PayPal' },
  ];

  const handleStartUpgrade = (plan: any) => {
    setSelectedPlan(plan);
    setStep('checkout');
  };

  const handleExecutePayment = () => {
    if (!paypalEmail) {
      alert("Please provide your PayPal email.");
      return;
    }
    setStep('processing');

    setTimeout(async () => {
      try {
        if (user) {
          const docRef = doc(db, 'vendors', user.uid);
          await setDoc(docRef, {
            uid: user.uid,
            subscriptionTier: selectedPlan.id,
            paypalEmail: paypalEmail,
            paymentStatus: 'active',
            updatedAt: serverTimestamp()
          }, { merge: true });

          addToast({
            title: 'SaaS Plan Active!',
            message: `Successfully upgraded to the ${selectedPlan.name} using PayPal secure sandbox billing.`,
            type: 'success'
          });

          setStep('success');
        }
      } catch (err) {
        console.error("In-app upgrade failed:", err);
        setStep('tiers');
      }
    }, 2500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs" id="in-app-billing-root">
      <Card className="bg-white border border-gray-150 rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* HEADER BAR */}
        <div className="bg-indigo-900 px-6 py-4 flex justify-between items-center text-white">
          <div>
            <h3 className="font-extrabold text-sm flex items-center gap-1.5 uppercase tracking-wider">
              <Zap className="w-4 h-4 text-amber-400 animate-pulse" />
              SaaS Subscription Plan Manager
            </h3>
            <p className="text-[10px] text-indigo-200 mt-0.5">Configure your SaaS membership tier, view PayPal invoices, and manage public sector tools.</p>
          </div>
          <button 
            onClick={onClose}
            className="p-1.5 text-indigo-200 hover:text-white transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* CONTAINER CONTENT */}
        <div className="p-6 overflow-y-auto space-y-8 flex-1">
          {step === 'tiers' && (
            <div className="space-y-8">
              {/* Active Plan Summary */}
              <div className="p-4 bg-indigo-50/50 border border-indigo-150 rounded-xl flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                <div className="space-y-0.5">
                  <p className="text-[10px] text-indigo-500 font-bold uppercase tracking-wider">Your Active Tier</p>
                  <h4 className="text-sm font-black text-indigo-950 flex items-center gap-1.5">
                    {vendorProfile?.subscriptionTier ? `${vendorProfile.subscriptionTier.toUpperCase()} MEMBERSHIP` : 'FREE TRIAL / PREVIEW MODE'}
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  </h4>
                  <p className="text-[11px] text-gray-500">
                    {vendorProfile?.subscriptionTier 
                      ? `Your account is upgraded and has unlocked premium ${vendorProfile.subscriptionTier === 'pro' ? 'Pro' : 'Enterprise'} areas.` 
                      : 'You are viewing standard opportunities. Upgrade to Pro to unlock California, Texas, and New York crawler databases.'}
                  </p>
                </div>
                <div className="text-xs font-semibold text-gray-500 flex flex-col text-right">
                  <span>Invoicing Method: {vendorProfile?.paypalEmail ? 'PayPal Sandbox' : 'Not setup'}</span>
                  <span className="text-emerald-600 font-bold">Billing Status: Active</span>
                </div>
              </div>

              {/* Grid plans */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {plans.map((plan) => {
                  const isActive = vendorProfile?.subscriptionTier === plan.id || (!vendorProfile?.subscriptionTier && plan.id === 'free');
                  return (
                    <div 
                      key={plan.id}
                      className={`p-5 rounded-xl border flex flex-col justify-between ${plan.popular ? 'border-indigo-500 ring-2 ring-indigo-500/10 bg-indigo-50/5' : 'border-gray-200'}`}
                    >
                      <div className="space-y-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-extrabold text-sm text-gray-900">{plan.name}</h4>
                            <p className="text-[10px] text-gray-400 mt-0.5">{plan.tagline}</p>
                          </div>
                          {isActive && (
                            <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-150">
                              Active Plan
                            </span>
                          )}
                        </div>

                        <div className="text-xl font-black text-gray-950">{plan.price}</div>
                        <div className="h-px bg-gray-100" />

                        <ul className="space-y-2.5">
                          {plan.features.map(f => (
                            <li key={f} className="flex gap-1.5 items-start text-[11px] text-gray-600">
                              <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="pt-6">
                        {isActive ? (
                          <button 
                            disabled
                            className="w-full py-2 text-xs font-bold bg-gray-50 text-gray-400 border border-gray-150 rounded-lg cursor-not-allowed"
                          >
                            Current Plan Tier
                          </button>
                        ) : (
                          <button 
                            id={`btn-inapp-purchase-${plan.id}`}
                            onClick={() => handleStartUpgrade(plan)}
                            className={`w-full py-2.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${plan.popular ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
                          >
                            Upgrade via PayPal
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* INVOICES SECTION */}
              {vendorProfile?.paypalEmail && (
                <div className="space-y-3 pt-4 border-t border-gray-100">
                  <h4 className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                    <FileText className="w-4 h-4 text-indigo-600" />
                    Secure PayPal Corporate Invoices
                  </h4>
                  <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 text-[10px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                        <tr>
                          <th className="px-5 py-3">Invoice Number</th>
                          <th className="px-5 py-3">Billing Cycle Date</th>
                          <th className="px-5 py-3">Amount</th>
                          <th className="px-5 py-3">Method</th>
                          <th className="px-5 py-3">Status</th>
                          <th className="px-5 py-3 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {mockInvoices.map((inv) => (
                          <tr key={inv.id} className="hover:bg-slate-50/50">
                            <td className="px-5 py-3 font-mono font-bold text-gray-700">{inv.id}</td>
                            <td className="px-5 py-3 text-gray-600">{inv.date}</td>
                            <td className="px-5 py-3 font-semibold text-gray-950">{inv.amount}</td>
                            <td className="px-5 py-3 text-gray-500">{inv.method}</td>
                            <td className="px-5 py-3">
                              <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100">
                                {inv.status}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-right">
                              <button 
                                onClick={() => alert(`Opening secure printable tax receipt for ${inv.id}...`)}
                                className="text-[11px] text-indigo-600 hover:text-indigo-800 font-bold"
                              >
                                View PDF Tax Invoice
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 'checkout' && selectedPlan && (
            <div className="max-w-md mx-auto space-y-6">
              <div className="bg-[#003087] px-6 py-4 flex justify-between items-center text-white rounded-t-xl">
                <div className="flex items-center gap-1.5">
                  <span className="font-black italic text-lg tracking-tight text-white flex items-center">
                    Pay
                    <span className="text-[#0079C1]">Pal</span>
                  </span>
                  <span className="text-[10px] font-bold tracking-widest uppercase text-slate-300 border-l border-slate-500 pl-2.5 ml-2.5">
                    Secure checkout
                  </span>
                </div>
              </div>

              <div className="p-6 bg-slate-50 border border-slate-150 rounded-b-xl space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-[10px] text-gray-400 font-bold uppercase">ORDER Presets</span>
                    <h4 className="font-extrabold text-gray-950 text-sm mt-0.5">{selectedPlan.name} Subscription</h4>
                  </div>
                  <span className="text-sm font-black text-indigo-600 bg-indigo-50 border border-indigo-100 px-2.5 py-0.5 rounded-md">
                    {selectedPlan.price}
                  </span>
                </div>

                <div className="space-y-4 pt-2">
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-gray-600">PayPal Account Email</p>
                    <input 
                      id="inapp-paypal-email"
                      type="email"
                      placeholder="paypal-corporate@your-firm.com"
                      value={paypalEmail}
                      onChange={(e) => setPaypalEmail(e.target.value)}
                      className="w-full text-xs p-3 bg-white border border-gray-200 rounded-xl focus:border-[#003087] outline-none transition-all"
                    />
                  </div>

                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-gray-600">PayPal Password</p>
                    <input 
                      id="inapp-paypal-password"
                      type="password"
                      placeholder="••••••••"
                      value={paypalPassword}
                      onChange={(e) => setPaypalPassword(e.target.value)}
                      className="w-full text-xs p-3 bg-white border border-gray-200 rounded-xl focus:border-[#003087] outline-none transition-all"
                    />
                  </div>

                  <div className="flex gap-2 items-center text-[10px] text-gray-400">
                    <Info className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>PayPal Secure Sandboxed transaction. Your live client scopes will adjust instantly.</span>
                  </div>

                  <div className="pt-2 flex gap-2">
                    <button 
                      onClick={() => setStep('tiers')}
                      className="flex-1 py-3 text-xs font-bold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl cursor-pointer"
                    >
                      Back
                    </button>
                    <button 
                      id="btn-inapp-pay-now"
                      onClick={handleExecutePayment}
                      className="flex-1 py-3 text-xs font-bold text-white bg-[#0079C1] hover:bg-[#005ea6] rounded-xl cursor-pointer shadow-sm text-center"
                    >
                      Authorize Payment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'processing' && (
            <div className="py-20 flex flex-col items-center justify-center space-y-4">
              <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
              <div className="text-center">
                <h4 className="text-xs font-bold text-gray-950">Validating PayPal authorization tokens...</h4>
                <p className="text-[10px] text-gray-400 mt-1">Executing mock sandbox clearing protocols and writing updated client scopes...</p>
              </div>
            </div>
          )}

          {step === 'success' && selectedPlan && (
            <div className="max-w-md mx-auto text-center space-y-6 py-6">
              <div className="w-12 h-12 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <ShieldCheck className="w-6 h-6 animate-pulse" />
              </div>

              <div className="space-y-1">
                <h4 className="text-sm font-bold text-gray-950">Subscription Cleared & Membership Active!</h4>
                <p className="text-xs text-gray-500">Your account tier has been successfully transitioned to the <span className="font-bold text-indigo-600">{selectedPlan.name}</span>.</p>
              </div>

              <div className="p-4 bg-emerald-50 border border-emerald-150 rounded-xl text-left text-[11px] text-emerald-950 space-y-1.5">
                <p className="flex justify-between">
                  <span>Merchant Transaction Receipt:</span>
                  <span className="font-mono font-bold">#PayPal-{Math.floor(100000 + Math.random() * 900000)}</span>
                </p>
                <p className="flex justify-between">
                  <span>Activated Account:</span>
                  <span>{user?.email}</span>
                </p>
              </div>

              <button 
                id="btn-inapp-done"
                onClick={onClose}
                className="w-full py-3.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl cursor-pointer shadow-xs"
              >
                Access Active Membership Area
              </button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
